import * as React from 'react'
import { AlertTriangle, Cable, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  applyGatewayLinkPreset,
  defaultGatewayLinkRoute,
  gatewayLinkRouteInput,
  useDeleteGatewayLinkRouteMutation,
  useGetAuthzMeQuery,
  useListGatewayLinkRoutesQuery,
  useUpsertGatewayLinkRouteMutation,
  type GatewayLinkPreset,
  type GatewayLinkReadMode,
  type GatewayLinkRoute,
  type GatewayLinkRouteInput
} from '@axilon-apps/domains-ontology-ui-state'

import { StatusPill } from '../../../components/primitives/StatusPill'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select'

type ReadModeKey =
  | 'acquisitionMode'
  | 'tagReadMode'
  | 'historyMode'
  | 'alarmMode'
  | 'opcMode'
  | 'databaseMode'
  | 'healthMode'

const READ_MODES: ReadonlyArray<{
  key: ReadModeKey
  label: string
  capability: string
}> = [
  { key: 'acquisitionMode', label: 'Acquisition', capability: 'subscriptions' },
  { key: 'tagReadMode', label: 'Current tags', capability: 'tags' },
  { key: 'historyMode', label: 'Tag history', capability: 'tag-history' },
  { key: 'alarmMode', label: 'Alarms', capability: 'alarms' },
  { key: 'opcMode', label: 'OPC relay', capability: 'opc' },
  { key: 'databaseMode', label: 'Databases', capability: 'databases' },
  { key: 'healthMode', label: 'Gateway health', capability: 'gateway-health' }
]

const PRESETS: ReadonlyArray<{
  value: GatewayLinkPreset
  label: string
  description: string
}> = [
  {
    value: 'webdev',
    label: 'Legacy WebDev',
    description: 'Keep every read and write on the existing WebDev path.'
  },
  {
    value: 'shadow',
    label: 'Shadow reads',
    description: 'WebDev stays authoritative while Gateway Link comparisons run in the background.'
  },
  {
    value: 'gateway_link_reads',
    label: 'Gateway Link reads',
    description: 'Move reads to Gateway Link while writes stay on WebDev.'
  },
  {
    value: 'gateway_link_all',
    label: 'Gateway Link all',
    description: 'Move reads and approved writes to Gateway Link.'
  }
]

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as { data?: unknown }).data
    if (data && typeof data === 'object' && 'message' in data) {
      const message = (data as { message?: unknown }).message
      if (typeof message === 'string') return message
    }
  }
  return 'Gateway Link request failed'
}

function formatTimestamp(value: string | null): string {
  if (!value) return '—'
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString()
}

function RouteEditor({
  route,
  canManage,
  busy,
  onSave,
  onDelete
}: {
  route: GatewayLinkRoute
  canManage: boolean
  busy: boolean
  onSave: (route: GatewayLinkRouteInput) => Promise<void>
  onDelete: (route: GatewayLinkRoute) => Promise<void>
}) {
  const [draft, setDraft] = React.useState(() => gatewayLinkRouteInput(route))
  const usesGatewayLinkOnly =
    READ_MODES.some(({ key }) => draft[key] === 'gateway_link') ||
    draft.writeMode === 'gateway_link'
  const usesShadow = READ_MODES.some(({ key }) => draft[key] === 'shadow')

  const applyPreset = (preset: GatewayLinkPreset) => {
    setDraft((current) => applyGatewayLinkPreset(current, preset))
  }

  const setReadMode = (key: ReadModeKey, mode: GatewayLinkReadMode) => {
    setDraft((current) => ({ ...current, [key]: mode }))
  }

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardContent className="flex flex-col gap-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-semibold text-ink">{route.gatewayId}</span>
              <StatusPill status={route.online ? 'run' : 'idle'}>
                {route.online ? 'module online' : 'module offline'}
              </StatusPill>
            </div>
            <div className="mt-1 truncate font-mono text-xs text-ink-3">{route.gatewayUrl}</div>
          </div>
          {canManage ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Remove route ${route.gatewayId}`}
              disabled={busy}
              onClick={() => void onDelete(route)}
            >
              <Trash2 className="size-3.5" />
            </Button>
          ) : null}
        </div>

        <div className="grid gap-2 rounded-md border border-border bg-muted/30 p-3 text-xs sm:grid-cols-3">
          <div>
            <div className="text-ink-3">Session</div>
            <div className="mt-0.5 truncate font-mono text-ink">{route.sessionId ?? 'none'}</div>
          </div>
          <div>
            <div className="text-ink-3">Fence epoch</div>
            <div className="mt-0.5 font-mono text-ink">{route.fenceEpoch ?? '—'}</div>
          </div>
          <div>
            <div className="text-ink-3">Lease expires</div>
            <div className="mt-0.5 text-ink">{formatTimestamp(route.sessionExpiresAt)}</div>
          </div>
          <div className="sm:col-span-3">
            <div className="text-ink-3">Negotiated capabilities</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {route.negotiatedCapabilities.length > 0 ? (
                route.negotiatedCapabilities.map((capability) => (
                  <Badge key={capability} variant="outline" className="font-mono text-10">
                    {capability}
                  </Badge>
                ))
              ) : (
                <span className="text-ink-3">No active capability negotiation</span>
              )}
            </div>
          </div>
        </div>

        {!route.online && usesGatewayLinkOnly ? (
          <div
            role="alert"
            className="flex gap-2 rounded-md border border-st-warn/30 bg-st-warn-bg px-3 py-2 text-xs text-st-warn"
          >
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            Gateway Link is selected while the module is offline. Gateway Link-only operations fail
            closed; switch back to Legacy WebDev to restore the old path.
          </div>
        ) : !route.online && usesShadow ? (
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-ink-3">
            WebDev remains authoritative. Shadow comparisons resume automatically when the module
            reconnects.
          </div>
        ) : null}

        <div>
          <div className="mb-2 text-xs font-medium text-ink">Cutover preset</div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {PRESETS.map((preset) => (
              <Button
                key={preset.value}
                type="button"
                variant="outline"
                className="h-auto items-start justify-start px-3 py-2 text-left"
                disabled={!canManage || busy}
                title={preset.description}
                onClick={() => applyPreset(preset.value)}
              >
                <span className="flex flex-col items-start">
                  <span>{preset.label}</span>
                  <span className="font-normal text-ink-3">{preset.description}</span>
                </span>
              </Button>
            ))}
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          {READ_MODES.map(({ key, label, capability }) => (
            <div
              key={key}
              className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2"
            >
              <div className="min-w-0">
                <div className="text-xs font-medium text-ink">{label}</div>
                <div
                  className={
                    draft[key] !== 'webdev' && !route.negotiatedCapabilities.includes(capability)
                      ? 'truncate text-10 text-st-warn'
                      : 'truncate font-mono text-10 text-ink-3'
                  }
                >
                  {draft[key] !== 'webdev' && !route.negotiatedCapabilities.includes(capability)
                    ? 'not negotiated'
                    : capability}
                </div>
              </div>
              <Select
                value={draft[key]}
                disabled={!canManage || busy}
                onValueChange={(value) => setReadMode(key, value as GatewayLinkReadMode)}
              >
                <SelectTrigger size="sm" className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="webdev">WebDev</SelectItem>
                  <SelectItem value="shadow">Shadow</SelectItem>
                  <SelectItem value="gateway_link">Gateway Link</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ))}
          <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
            <div>
              <div className="text-xs font-medium text-ink">Approved writes</div>
              <div className="text-10 text-ink-3">Never shadowed or retried cross-backend</div>
            </div>
            <Select
              value={draft.writeMode}
              disabled={!canManage || busy}
              onValueChange={(value) =>
                setDraft((current) => ({
                  ...current,
                  writeMode: value as GatewayLinkRouteInput['writeMode']
                }))
              }
            >
              <SelectTrigger size="sm" className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="webdev">WebDev</SelectItem>
                <SelectItem value="gateway_link">Gateway Link</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            disabled={!canManage || busy}
            onClick={() => void onSave(draft)}
          >
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : null}
            Save routing
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

export function GatewayLinkTab() {
  const authz = useGetAuthzMeQuery()
  const canManage = authz.data?.permissions.includes('acquisition/manage') === true
  const routesQuery = useListGatewayLinkRoutesQuery(undefined, { pollingInterval: 15_000 })
  const [upsertRoute, upsertState] = useUpsertGatewayLinkRouteMutation()
  const [deleteRoute, deleteState] = useDeleteGatewayLinkRouteMutation()
  const [gatewayId, setGatewayId] = React.useState('')
  const [gatewayUrl, setGatewayUrl] = React.useState('')
  const [formError, setFormError] = React.useState('')
  const busy = upsertState.isLoading || deleteState.isLoading

  const save = async (route: GatewayLinkRouteInput) => {
    try {
      await upsertRoute(route).unwrap()
      toast.success('Gateway routing updated')
    } catch (error) {
      toast.error(errorMessage(error))
    }
  }

  const remove = async (route: GatewayLinkRoute) => {
    if (!window.confirm(`Remove Gateway Link routing for ${route.gatewayId}?`)) return
    try {
      await deleteRoute(route.gatewayId).unwrap()
      toast.success('Gateway routing removed; WebDev defaults apply')
    } catch (error) {
      toast.error(errorMessage(error))
    }
  }

  const addLegacyRoute = async () => {
    const id = gatewayId.trim()
    const url = gatewayUrl.trim().replace(/\/+$/, '')
    if (!id || !url) {
      setFormError('Gateway ID and URL are required')
      return
    }
    setFormError('')
    try {
      await upsertRoute(defaultGatewayLinkRoute(id, url)).unwrap()
      setGatewayId('')
      setGatewayUrl('')
      toast.success('Gateway added in Legacy WebDev mode')
    } catch (error) {
      setFormError(errorMessage(error))
    }
  }

  return (
    <div className="flex flex-col gap-card">
      <Card className="gap-3 py-4">
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
                <Cable className="size-4" />
                Gateway Link routing
              </h3>
              <p className="mt-1 max-w-3xl text-xs text-ink-3">
                Start on WebDev, compare reads in Shadow, then move each capability to Gateway Link.
                Writes always select one backend and never shadow or cross-retry.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={routesQuery.isFetching}
              onClick={() => void routesQuery.refetch()}
            >
              <RefreshCw
                className={routesQuery.isFetching ? 'size-3.5 animate-spin' : 'size-3.5'}
              />
              Refresh diagnostics
            </Button>
          </div>
          {!canManage && !authz.isLoading ? (
            <div className="rounded-md bg-muted px-3 py-2 text-xs text-ink-3">
              Routing is read-only. The <code>acquisition/manage</code> permission is required to
              change modes.
            </div>
          ) : null}
        </CardContent>
      </Card>

      {routesQuery.isLoading ? (
        <div className="flex items-center gap-2 text-xs text-ink-3">
          <Loader2 className="size-3.5 animate-spin" />
          Loading Gateway Link routes…
        </div>
      ) : routesQuery.error ? (
        <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
          {errorMessage(routesQuery.error)}
        </div>
      ) : (
        (routesQuery.data ?? []).map((route) => (
          <RouteEditor
            key={`${route.gatewayId}:${route.updatedAt}`}
            route={route}
            canManage={canManage}
            busy={busy}
            onSave={save}
            onDelete={remove}
          />
        ))
      )}

      {canManage ? (
        <Card className="gap-3 py-4">
          <CardContent className="flex flex-col gap-3">
            <div>
              <h3 className="text-sm font-semibold text-ink">Add gateway routing</h3>
              <p className="text-xs text-ink-3">
                New gateways start entirely on WebDev, so adding posture does not cut over traffic.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="gateway-link-id">Gateway ID</Label>
                <Input
                  id="gateway-link-id"
                  className="font-mono text-xs"
                  placeholder="Ignition gateway UUID"
                  value={gatewayId}
                  disabled={busy}
                  onChange={(event) => setGatewayId(event.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="gateway-link-url">WebDev gateway URL</Label>
                <Input
                  id="gateway-link-url"
                  className="font-mono text-xs"
                  placeholder="https://gateway.example.com:8043"
                  value={gatewayUrl}
                  disabled={busy}
                  onChange={(event) => setGatewayUrl(event.target.value)}
                />
              </div>
            </div>
            {formError ? (
              <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
                {formError}
              </div>
            ) : null}
            <div className="flex justify-end">
              <Button type="button" size="sm" disabled={busy} onClick={() => void addLegacyRoute()}>
                <Plus className="size-3.5" />
                Add in WebDev mode
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}
